import { BN } from '@coral-xyz/anchor'
import { Program } from '@coral-xyz/anchor'
import {
  PDA,
  SolCatContext,
  SolCatStakingType,
  StakingClient,
  UserFlexibleStakingInfo,
  UserLockupStakingInfo,
  V2Context,
  V2StakingType,
} from '@catsolhat/staking-sdk'
import { PublicKey } from '@solana/web3.js'
import projectConfig from './project-config'

const solcatAddress = projectConfig.SOLCAT.address

export class MultiVersionStakingClient extends StakingClient {
  constructor(
    solCatCtx: SolCatContext,
    v2Ctx: V2Context,
    programV1: Program<SolCatStakingType>,
    programV2: Program<V2StakingType>,
  ) {
    super(solCatCtx, v2Ctx, new PDA(programV1.programId), new PDA(programV2.programId))
  }

  // USER LOCKUP

  async getUserLockupStakingInfo(filter: {
    unlocked?: boolean
    usingV2: boolean
    mint?: PublicKey
  }): Promise<UserLockupStakingInfo> {
    // Don't use mint param if using v1
    const solcatResult = await super.getUserLockupStakingInfo({
      unlocked: false,
      usingV2: false,
    })
    console.log('solcat lockup', solcatResult)

    const v2Result = await super.getUserLockupStakingInfo({
      unlocked: false,
      usingV2: true,
    })

    console.log('v2 lockup', v2Result)

    // Create new result object starting with solcat data
    const result: UserLockupStakingInfo = {}
    if (solcatResult[solcatAddress]) {
      result[solcatAddress] = solcatResult[solcatAddress]
    }

    // Add v2 results (excluding any solcat token in v2 vaults)
    Object.keys(v2Result).forEach((tokenAddress) => {
      if (tokenAddress !== solcatAddress) {
        result[tokenAddress] = v2Result[tokenAddress]
      }
    })

    console.log('v2 lockup merged', result)

    return result
  }

  async lockup(amount: BN, vaultIndex: number, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('lockup v1', amount.toString(), vaultIndex, usingV2, mint.toBase58())
      return await super.lockup(amount, vaultIndex, false)
    }
    console.log('lockup v2', amount.toString(), vaultIndex, usingV2, mint?.toBase58())
    return await super.lockup(amount, vaultIndex, usingV2, mint)
  }

  async unlock(index: number, vaultIndex: number, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('unlock v1', index, vaultIndex, usingV2, mint.toBase58())
      return await super.unlock(index, vaultIndex, false)
    }
    console.log('unlock v2', index, vaultIndex, usingV2, mint?.toBase58())
    return await super.unlock(index, vaultIndex, usingV2, mint)
  }

  // USER FLEXIBLE

  async getUserFlexibleStakingInfo({ withdrew, usingV2 }: { withdrew?: boolean; usingV2: boolean }) {
    // Don't use mint param if using v1
    const solcatResult = await super.getUserFlexibleStakingInfo({
      withdrew,
      usingV2: false,
    })
    console.log('solcat flexible', solcatResult)

    const v2Result = await super.getUserFlexibleStakingInfo({
      withdrew,
      usingV2: true,
    })
    console.log('v2 flexible', v2Result)

    // Create new result object starting with solcat data
    const result: UserFlexibleStakingInfo = {}
    if (solcatResult[solcatAddress]) {
      result[solcatAddress] = solcatResult[solcatAddress]
    }

    // Add v2 results (excluding any solcat token in v2 vaults)
    Object.keys(v2Result).forEach((tokenAddress) => {
      if (tokenAddress !== solcatAddress) {
        result[tokenAddress] = v2Result[tokenAddress]
      }
    })

    console.log('v2 flexible merged', result)

    return result
  }

  async stake(amount: BN, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('stake v1', amount, usingV2, mint.toBase58())
      return await super.stake(amount, false)
    }
    console.log('stake v2', amount, usingV2, mint?.toBase58())
    return await super.stake(amount, usingV2, mint)
  }

  async withdraw(amount: BN, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('withdraw v1', amount, usingV2, mint.toBase58())
      return await super.withdraw(amount, false)
    }
    console.log('withdraw v2', amount, usingV2, mint?.toBase58())
    return await super.withdraw(amount, usingV2, mint)
  }

  async withdrawAfterDelay(index: number, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('withdrawAfterDelay v1', index, usingV2, mint.toBase58())
      return await super.withdrawAfterDelay(index, false)
    }
    console.log('withdrawAfterDelay v2', index, usingV2, mint?.toBase58())
    return await super.withdrawAfterDelay(index, usingV2, mint)
  }

  async claim(usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('claim v1', usingV2, mint.toBase58())
      return await super.claim(false)
    }
    console.log('claim v2', usingV2, mint?.toBase58())
    return await super.claim(usingV2, mint)
  }

  // ADMIN

  async updateAdmin(newAdmin: PublicKey, usingV2?: boolean) {
    console.log('updateAdmin' + (usingV2 ? 'v2' : 'v1'), newAdmin.toBase58())
    return await super.updateAdmin(newAdmin, usingV2)
  }

  async depositFund(amount: BN, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('depositFund v1', amount, usingV2, mint.toBase58())
      return await super.depositFund(amount, false)
    }
    console.log('depositFund v2', amount, usingV2, mint?.toBase58())
    return await super.depositFund(amount, usingV2, mint)
  }

  async withdrawFund(amount: BN, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('withdrawFund v1', amount, usingV2, mint.toBase58())
      return await super.withdrawFund(amount, false)
    }
    console.log('withdrawFund v2', amount, usingV2, mint?.toBase58())
    return await super.withdrawFund(amount, usingV2, mint)
  }

  async updateLockupVault(index: number, apy: number, lockupDuration: BN, minStakeAmount: BN, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('updateLockupVault v1', index, apy, lockupDuration, minStakeAmount, usingV2, mint.toBase58())
      return await super.updateLockupVault(index, apy, lockupDuration, minStakeAmount, false)
    }
    console.log('updateLockupVault v2', index, apy, lockupDuration, minStakeAmount, usingV2, mint?.toBase58())
    return await super.updateLockupVault(index, apy, lockupDuration, minStakeAmount, usingV2, mint)
  }

  async updateFlexibleVault(rewardRate: BN, withdrawalDelayTime: BN, usingV2?: boolean, mint?: PublicKey) {
    if (mint && mint.toBase58() === solcatAddress) {
      console.log('updateFlexibleVault v1', rewardRate, withdrawalDelayTime, usingV2, mint.toBase58())
      return await super.updateFlexibleVault(rewardRate, withdrawalDelayTime, false)
    }
    console.log('updateFlexibleVault v2', rewardRate, withdrawalDelayTime, usingV2, mint?.toBase58())
    return await super.updateFlexibleVault(rewardRate, withdrawalDelayTime, usingV2, mint)
  }
}
